/**
 * Multi-section schema normalization.
 *
 * Transforms an `ExternalTableSchema` that has `sections[]` into an
 * `InternalMultiSectionSchema` by normalizing each section as a standalone
 * table schema and normalizing the shared controls.
 */

import { normalizeSchema, normalizeAction, normalizeActionList } from './normalizer';
import type {
  ExternalTableSchema,
  SectionDef,
  SharedControlsDef,
  InternalMultiSectionSchema,
  InternalSection,
  InternalSharedControls,
  FeaturesConfig,
  ActionConfig,
  DataSourceConfig,
  SectionExpandMode,
} from './types';

function deepMergeFeaturesDefaults(
  sectionFeatures: FeaturesConfig | undefined,
  rootFeatures: FeaturesConfig | undefined,
): FeaturesConfig | undefined {
  if (!rootFeatures) return sectionFeatures;
  if (!sectionFeatures) return rootFeatures;
  const merged: FeaturesConfig = { ...rootFeatures };
  for (const key of Object.keys(sectionFeatures) as (keyof FeaturesConfig)[]) {
    const sv = sectionFeatures[key];
    if (sv !== undefined) {
      (merged as Record<string, unknown>)[key] = sv;
    }
  }
  return merged;
}

function mergeActionsDefaults(
  sectionActions: ActionConfig | undefined,
  rootActions: ActionConfig | undefined,
): ActionConfig | undefined {
  if (!rootActions) return sectionActions;
  if (!sectionActions) return rootActions;
  return {
    row: sectionActions.row ?? rootActions.row,
    bulk: sectionActions.bulk ?? rootActions.bulk,
    toolbar: sectionActions.toolbar ?? rootActions.toolbar,
  };
}

function mergeDataSourceDefaults(
  sectionDs: DataSourceConfig | undefined,
  rootDs: DataSourceConfig | undefined,
): DataSourceConfig | undefined {
  if (!rootDs) return sectionDs;
  if (!sectionDs) return rootDs;
  return {
    ...rootDs,
    ...sectionDs,
    filters: sectionDs.filters ?? rootDs.filters,
  };
}

function buildSyntheticSchema(
  section: SectionDef,
  root: ExternalTableSchema,
): ExternalTableSchema {
  const mergedFeatures = deepMergeFeaturesDefaults(section.features, root.features);

  // Suffix the persistence key with section ID so each section stores independently
  if (mergedFeatures?.columnPreferences?.persistenceKey) {
    mergedFeatures.columnPreferences = {
      ...mergedFeatures.columnPreferences,
      persistenceKey: `${mergedFeatures.columnPreferences.persistenceKey}:${section.id}`,
    };
  }

  return {
    metadata: { title: section.title, entity: section.id },
    columns: section.columns,
    dataSource: mergeDataSourceDefaults(section.dataSource, root.dataSource),
    actions: mergeActionsDefaults(section.actions, root.actions),
    features: mergedFeatures,
    computeRules: section.computeRules ?? root.computeRules,
  };
}

function normalizeSharedControls(
  controls: SharedControlsDef | undefined,
): InternalSharedControls {
  const c = controls ?? {};
  return {
    search: {
      enabled: c.search?.enabled ?? true,
      placeholder: c.search?.placeholder ?? 'Search…',
      debounce: c.search?.debounce ?? 300,
    },
    filters: [...(c.filters ?? [])],
    toolbar: {
      primary: c.toolbar?.primary
        ? normalizeAction(c.toolbar.primary, 0, 'shared-toolbar-primary')
        : null,
      secondary: normalizeActionList(c.toolbar?.secondary, 'shared-toolbar-secondary'),
    },
    import: {
      downloadTemplate: c.import?.downloadTemplate
        ? normalizeAction(c.import.downloadTemplate, 0, 'shared-import-download')
        : null,
      uploadAction: c.import?.uploadAction
        ? normalizeAction(c.import.uploadAction, 0, 'shared-import-upload')
        : null,
    },
  };
}

function resolveInitialExpandedSections(
  mode: SectionExpandMode | undefined,
  sections: InternalSection[],
): string[] {
  if (mode === undefined || mode === 'allCollapsed') {
    return [];
  }
  if (mode === 'allExpanded') {
    return sections.map((s) => s.id);
  }
  if (mode === 'firstExpanded') {
    return sections.length > 0 ? [sections[0].id] : [];
  }
  if (Array.isArray(mode)) {
    const validIds = new Set(sections.map((s) => s.id));
    return mode.filter((id) => validIds.has(id));
  }
  return [];
}

export function normalizeMultiSectionSchema(
  external: ExternalTableSchema,
): InternalMultiSectionSchema {
  const sections = external.sections ?? [];
  const meta = external.metadata ?? {};
  const expandMode = external.sharedControls?.sectionExpandMode;

  const normalizedSections: InternalSection[] = sections.map((section) => {
    const syntheticSchema = buildSyntheticSchema(section, external);
    const tableSchema = normalizeSchema(syntheticSchema);

    return {
      id: section.id,
      title: section.title,
      defaultExpanded: section.defaultExpanded ?? false,
      permission: section.permission
        ? { action: section.permission.action, resource: section.permission.resource }
        : null,
      tableSchema,
    };
  });

  let initialExpanded = resolveInitialExpandedSections(expandMode, normalizedSections);

  // Legacy fallback: if no sectionExpandMode is set, honor per-section defaultExpanded
  if (expandMode === undefined) {
    const fromLegacy = normalizedSections.filter((s) => s.defaultExpanded).map((s) => s.id);
    if (fromLegacy.length > 0) {
      initialExpanded = fromLegacy;
    }
  }

  return Object.freeze({
    kind: 'multiSection' as const,
    version: '2.0' as const,
    title: meta.title ?? '',
    description: meta.description ?? '',
    sections: Object.freeze(normalizedSections),
    sharedControls: Object.freeze(normalizeSharedControls(external.sharedControls)),
    initialExpandedSections: Object.freeze(initialExpanded),
    testIds: Object.freeze({}),
  });
}
